import type { AbstractEngine } from "@babylonjs/core/Engines/abstractEngine";
import { Engine } from "@babylonjs/core/Engines/engine";
import { WebGPUEngine } from "@babylonjs/core/Engines/webgpuEngine";

import type { RendererPreference } from "@/config/parseEnv";
import { describeError, logger } from "@/utils/logger";

export type EngineBackend = "webgpu" | "webgl";

export interface CreateEngineOptions {
  readonly renderer: RendererPreference;
  readonly antialias: boolean;
}

export interface CreatedEngine {
  readonly engine: AbstractEngine;
  readonly backend: EngineBackend;
}

/**
 * Crée le moteur de rendu.
 *
 * En mode `auto`, WebGPU est tenté puis abandonné silencieusement au profit de
 * WebGL2 s'il n'est pas disponible ou si son initialisation échoue (pilote
 * bloqué, contexte refusé). Un backend explicitement demandé, lui, ne retombe
 * pas en silence : mieux vaut une erreur claire qu'un rendu qui n'est pas celui
 * qu'on croyait mesurer.
 */
export async function createEngine(
  canvas: HTMLCanvasElement,
  options: CreateEngineOptions,
): Promise<CreatedEngine> {
  const { renderer, antialias } = options;

  if (renderer === "auto" || renderer === "webgpu") {
    const webgpu = await tryCreateWebGPU(canvas, antialias, renderer === "webgpu");
    if (webgpu) return { engine: webgpu, backend: "webgpu" };
  }

  const engine = new Engine(
    canvas,
    antialias,
    {
      // Non requis pour un rendu classique ; le désactiver évite au navigateur
      // de conserver une copie du framebuffer à chaque image.
      preserveDrawingBuffer: false,
      stencil: true,
      powerPreference: "high-performance",
    },
    // Le ratio est piloté explicitement via setHardwareScalingLevel (voir Game).
    false,
  );

  return { engine, backend: "webgl" };
}

async function tryCreateWebGPU(
  canvas: HTMLCanvasElement,
  antialias: boolean,
  required: boolean,
): Promise<AbstractEngine | null> {
  const supported = await WebGPUEngine.IsSupportedAsync.catch(() => false);

  if (!supported) {
    if (required) {
      throw new Error(
        "WebGPU a été demandé explicitement mais n'est pas supporté par ce navigateur.",
      );
    }
    logger.info("WebGPU indisponible, repli sur WebGL2.");
    return null;
  }

  try {
    const engine = new WebGPUEngine(canvas, {
      antialias,
      stencil: true,
      powerPreference: "high-performance",
    });
    await engine.initAsync();
    return engine;
  } catch (error) {
    if (required) throw error;
    logger.warn(`Initialisation WebGPU échouée, repli sur WebGL2 : ${describeError(error)}`);
    return null;
  }
}
